// Suivi de la facturation : quels chantiers attendent une facture.
//
// Croise trois sources que rien ne reliait jusqu'ici :
//   l'AGENDA      pour savoir si un chantier est placé, et quand il se termine
//   les DEVIS     pour savoir ce que le client a signé — donc ce qui est engagé
//   les FACTURES  pour savoir ce qui a déjà été facturé, et ce qui est réglé
//
// Trois situations remontent, dans cet ordre de priorité :
//   1. l'intervention est passée et rien ne solde le chantier -> À FACTURER,
//      c'est de l'argent gagné qui n'est pas encore réclamé ;
//   2. le chantier est placé mais aucun acompte n'a été émis -> ACOMPTE À
//      FACTURER, c'est de la trésorerie engagée sans contrepartie ;
//   3. le chantier est ENGAGÉ — le client a signé — mais n'est pas à l'agenda,
//      et il reste quelque chose à facturer. Sans ce troisième cas, l'écran
//      resterait vide chez qui ne planifie pas ses chantiers dans le CRM : la
//      règle « placé à l'agenda » décrivait un usage, pas une vérité.
//
// DEUX RÈGLES DE COMPTAGE, apprises d'un écran qui affichait -9 299,83 € de
// « déjà facturé » :
//
//   un avoir et la facture qu'il annule s'effacent ENSEMBLE. La facture
//   annulée sort déjà du calcul (statut `annulee_avoir`) ; retrancher l'avoir
//   par-dessus compterait l'annulation deux fois.
//
//   le montant engagé vient du DEVIS SIGNÉ. Le champ « montant total HT » de
//   la fiche chantier existe, mais rien ne le remplit : il vaut 0 partout, et
//   un reste calculé dessus ne veut rien dire.
//
// Fonction PURE : aucun accès réseau ni base, donc testable ligne à ligne.

/** Un chantier, réduit à ce qui sert ici. */
export interface ChantierFacturable {
  id: string;
  numero: string;
  libelle: string;
  statut: string;
  contactId: string | null;
  /**
   * Montant engagé saisi sur la FICHE chantier. Aujourd'hui toujours 0 : aucun
   * écran du CRM ne l'alimente. Gardé parce qu'il fait autorité le jour où
   * quelqu'un le renseigne.
   */
  montantTotalHt: number;
}

/** Une intervention placée à l'agenda. */
export interface InterventionPlacee {
  chantierId: string | null;
  /** Fin de l'intervention, horodatage ISO complet (avec son décalage). */
  finAt: string;
}

/** Un devis rattaché à un chantier. */
export interface DevisRattache {
  id: string;
  chantierId: string | null;
  /** À qui ce devis est adressé — pas forcément le client du chantier. */
  contactId: string | null;
  numero: string;
  statut: string; // brouillon | a_envoyer | envoye | consulte | signe | …
  montantHt: number;
  montantTtc: number;
}

/** Une facture rattachée à un chantier. */
export interface FactureRattachee {
  id: string;
  chantierId: string | null;
  /** À qui cette facture est adressée — pas forcément le client du chantier. */
  contactId: string | null;
  numero: string;
  kind: string; // acompte | situation | finale | avoir | standard
  statut: string; // brouillon | emise | envoyee | payee | annulee_avoir | …
  /**
   * Pour un avoir : la facture qu'il annule (`factures.facture_liee_id`).
   * C'est ce lien qui évite de compter une annulation deux fois.
   */
  factureLieeId: string | null;
  /** Hors taxes : c'est ce qui se compare au montant engagé du chantier. */
  montantHt: number;
  montantTtc: number;
  /** Déjà encaissé, TTC — c'est TTC que le client règle. */
  montantPaye: number;
  /**
   * Pour une facture d'ACOMPTE : le total HT dont elle est un pourcentage,
   * calculé et figé à l'émission (`metadata.acompte.total_ht_origine`).
   *
   * C'est un engagement que le CRM connaît par lui-même, même quand le devis
   * n'a pas été rattaché au chantier. Null partout ailleurs.
   */
  montantOrigineHt: number | null;
}

/**
 * Un document du chantier, tel qu'on veut pouvoir y sauter depuis l'écran.
 *
 * Répondre « il reste 9 299,83 € à facturer » sans dire SUR QUOI oblige à
 * rouvrir trois écrans pour vérifier. La ligne porte donc ses pièces.
 */
export interface DocumentLie {
  id: string;
  type: 'devis' | 'facture';
  numero: string;
  /** acompte | finale | avoir | standard — vide pour un devis. */
  kind: string;
  statut: string;
  montantHt: number;
  montantTtc: number;
  /**
   * Ce document entre-t-il dans le calcul du déjà-facturé ? Un brouillon, une
   * facture annulée et l'avoir qui l'annule n'y entrent pas : l'écran doit
   * pouvoir le dire, sinon les chiffres semblent faux.
   */
  compte: boolean;
}

/**
 * Classement d'un chantier placé, fondé sur la date de sa dernière intervention
 * à l'agenda — et, DEPUIS QUE LES FACTURES SE FONT DANS LE CRM, sur ce qui a
 * réellement été facturé.
 *
 * La distinction se fait chantier par chantier, jamais en bloc :
 *
 *   - un chantier que le CRM suit (il porte un devis ou une facture d'ici) :
 *     on sait ce qui a été facturé, donc on sait ce qu'il reste, et un
 *     chantier entièrement facturé sort de la liste ;
 *   - un chantier d'avant, facturé dans Extrabat : le CRM n'en sait rien. Le
 *     reste vaut alors `null`, et l'écran le dit au lieu d'inventer un
 *     chiffre. Déclarer « à facturer » un chantier réglé depuis des mois
 *     serait pire que de se taire.
 */
export type MotifFacturation =
  | 'intervention_passee'
  | 'intervention_a_venir'
  /** Engagé par le client, mais pas encore posé à l'agenda. */
  | 'engage_non_place'
  /**
   * Tout est facturé ; il ne reste qu'à être payé. Ce motif passe AVANT les
   * trois autres : ce que le chantier attend, ce n'est plus une facture, c'est
   * un virement — et l'écran doit dire ce qu'il y a à faire, pas où en sont
   * les travaux.
   */
  | 'attente_reglement';

export interface LigneFacturation {
  chantierId: string;
  numero: string;
  libelle: string;
  statut: string;
  /** Le client du CHANTIER : chez qui les travaux se font. */
  contactId: string | null;
  /**
   * Le client à qui la facture ira, QUAND CE N'EST PAS celui du chantier —
   * sous-traitance : les travaux se font chez M. et Mme Untel, le devis est
   * adressé à l'entreprise qui a passé la commande.
   *
   * Null quand c'est le même : l'écran n'a alors rien à préciser. Sans cette
   * distinction, la ligne annonce le nom de l'occupant et l'on part facturer
   * la mauvaise personne.
   */
  contactFactureId: string | null;
  /** Fin de la dernière intervention placée (ISO), null si aucune date. */
  finIntervention: string | null;
  interventionPassee: boolean;
  /** Jours écoulés depuis la fin de l'intervention (négatif si à venir). */
  joursDepuis: number | null;
  /**
   * Le montant engagé sur ce chantier, hors taxes. NULL quand personne ne l'a
   * dit : ni fiche chantier renseignée, ni devis signé dans le CRM.
   */
  montantEngageHt: number | null;
  montantFactureTtc: number;
  /** Déjà facturé hors taxes, avoirs déduits : comparable au montant engagé. */
  montantFactureHt: number;
  /**
   * Ce qu'il reste à facturer, hors taxes. NULL quand le CRM ne suit pas ce
   * chantier : on ne sait pas, et on ne le cache pas derrière un zéro.
   */
  resteAFacturerHt: number | null;
  /**
   * Facturé mais pas encore réglé, TTC. Deux choses très différentes tiennent
   * dans cet écran : ce qui n'est pas encore RÉCLAMÉ (reste à facturer) et ce
   * qui est réclamé mais pas encore TOMBÉ (reste à encaisser). Un chantier
   * entièrement facturé et jamais payé affiche 0 d'un côté et tout de l'autre.
   */
  resteAEncaisserTtc: number;
  /**
   * Le même reste à encaisser, en HORS TAXES.
   *
   * Sert à la projection de trésorerie, qui raisonne en HT et rajoute la TVA
   * elle-même. Le règlement, lui, est un montant TTC : on le répartit donc au
   * prorata sur la part HT de chaque facture, plutôt que de le retrancher tel
   * quel d'un total HT — ce qui reviendrait à compter la TVA deux fois.
   */
  resteAEncaisserHt: number;
  /** Le CRM porte-t-il les documents de ce chantier ? */
  suiviParLeCrm: boolean;
  /** Le client a-t-il signé un devis de ce chantier ? */
  devisSigne: boolean;
  /**
   * Un acompte a-t-il été émis DANS LE CRM ? Faux ne veut pas dire « aucun
   * acompte » : il peut avoir été émis dans Extrabat, que le CRM ne sait pas
   * rattacher. Information, jamais critère de classement.
   */
  acompteEmis: boolean;
  /** Acompte commencé mais jamais émis : à ne pas confondre avec « rien fait ». */
  acompteBrouillon: boolean;
  /** Devis et factures du chantier, pour pouvoir y aller d'un clic. */
  documents: DocumentLie[];
  motif: MotifFacturation;
}

/** Chantiers hors jeu : ils n'attendent plus rien. */
const STATUTS_CLOS = new Set(['annule', 'archive']);

/** Une facture annulée ou en brouillon ne prouve aucune facturation émise. */
const STATUTS_NON_EMIS = new Set(['brouillon', 'annulee_avoir']);

/** Un devis à ces statuts-là engage le client : il a dit oui. */
const DEVIS_SIGNES = new Set(['signe', 'converti_facture']);

const JOUR_MS = 86_400_000;

/** Arrondi au centime, pour ne pas traîner les décimales du binaire. */
const centimes = (n: number): number => Math.round(n * 100) / 100;

/**
 * Les factures qui COMPTENT vraiment dans le déjà-facturé d'un chantier.
 *
 * Écarte les brouillons et les factures annulées, puis règle le cas de
 * l'avoir : émettre un avoir fait passer la facture d'origine en
 * `annulee_avoir`, donc déjà hors du calcul. Retrancher l'avoir par-dessus
 * compterait l'annulation deux fois et ferait plonger le total sous zéro.
 *
 * L'avoir n'est donc retenu que lorsqu'il retire vraiment de l'argent : quand
 * la facture qu'il vise compte encore, ou quand il n'en vise aucune.
 *
 * Extraite de la boucle le jour où le contrôle d'émission en a eu besoin :
 * deux copies de cette règle auraient fini par se contredire.
 */
export function facturesRetenues(fs: readonly FactureRattachee[]): FactureRattachee[] {
  const emises = fs.filter((f) => !STATUTS_NON_EMIS.has(f.statut));
  const comptees = new Set(emises.map((f) => f.id));
  return emises.filter(
    (f) => f.kind !== 'avoir' || f.factureLieeId === null || comptees.has(f.factureLieeId),
  );
}

/** Un avoir retire, tout le reste ajoute. */
const signeFacture = (f: FactureRattachee): number => (f.kind === 'avoir' ? -1 : 1);

/** Déjà facturé hors taxes, avoirs déduits. */
export function dejaFactureHt(retenues: readonly FactureRattachee[]): number {
  return centimes(retenues.reduce((t, f) => t + signeFacture(f) * Math.abs(f.montantHt), 0));
}

/**
 * CE QUE LE CHANTIER DOIT RAPPORTER EN TOUT, hors taxes. 0 quand nul ne le
 * sait.
 *
 * Trois sources, par ordre d'autorité décroissante :
 *
 *   la FICHE chantier, si quelqu'un l'a renseignée — aucun écran ne le fait
 *   aujourd'hui, mais elle ferait foi ;
 *   les DEVIS SIGNÉS, qui disent ce que le client a accepté. Deux devis signés
 *   s'ajoutent : chacun est un engagement de plus ;
 *   le total qu'un ACOMPTE a figé à son émission, quand le devis n'est pas
 *   rattaché au chantier.
 */
export function engagementHt(
  montantTotalHt: number,
  devisSignes: readonly DevisRattache[],
  retenues: readonly FactureRattachee[],
): number {
  if (montantTotalHt > 0) return montantTotalHt;
  const surDevis = centimes(devisSignes.reduce((t, d) => t + d.montantHt, 0));
  if (surDevis > 0) return surDevis;
  // Le plus grand des acomptes fait foi : deux acomptes successifs peuvent
  // avoir été calculés sur des assiettes différentes, et la plus large est la
  // moins fausse.
  return retenues.reduce(
    (t, f) => (f.montantOrigineHt !== null && f.montantOrigineHt > t ? f.montantOrigineHt : t),
    0,
  );
}

/** Regroupe par chantier, en laissant tomber ce qui n'y est pas rattaché. */
function parChantier<T extends { chantierId: string | null }>(items: T[]): Map<string, T[]> {
  const m = new Map<string, T[]>();
  for (const it of items) {
    if (!it.chantierId) continue;
    const l = m.get(it.chantierId);
    if (l) l.push(it);
    else m.set(it.chantierId, [it]);
  }
  return m;
}

/**
 * Dresse la liste des chantiers qui attendent une facture.
 *
 * `maintenantMs` est passé en paramètre plutôt que lu de l'horloge : la
 * fonction reste pure, et les tests peuvent se placer à n'importe quelle date.
 *
 * Les comparaisons de dates se font en millisecondes depuis l'époque, jamais
 * sur des morceaux de texte : l'horodatage porte son propre décalage horaire,
 * et découper « 2026-09-02T01:00:00+02:00 » à la main donnerait la veille.
 */
export function suiviFacturation(
  chantiers: ChantierFacturable[],
  interventions: InterventionPlacee[],
  factures: FactureRattachee[],
  devis: DevisRattache[],
  maintenantMs: number,
): LigneFacturation[] {
  // Fin de la DERNIÈRE intervention placée, chantier par chantier.
  const finParChantier = new Map<string, number>();
  for (const e of interventions) {
    if (!e.chantierId) continue;
    const t = Date.parse(e.finAt);
    if (Number.isNaN(t)) continue;
    const connu = finParChantier.get(e.chantierId);
    if (connu === undefined || t > connu) finParChantier.set(e.chantierId, t);
  }

  const facturesParChantier = parChantier(factures);
  const devisParChantier = parChantier(devis);

  const lignes: LigneFacturation[] = [];
  for (const ch of chantiers) {
    if (STATUTS_CLOS.has(ch.statut)) continue;
    const fin = finParChantier.get(ch.id);
    const fs = facturesParChantier.get(ch.id) ?? [];
    const ds = devisParChantier.get(ch.id) ?? [];

    // Le CRM porte-t-il les documents de ce chantier ? C'est ce qui autorise à
    // dire ce qu'il reste. Sur les chantiers d'avant, facturés dans Extrabat,
    // il ne sait rien et ne doit rien affirmer.
    const suiviParLeCrm = fs.length > 0 || ds.length > 0;
    const signes = ds.filter((d) => DEVIS_SIGNES.has(d.statut));
    const devisSigne = signes.length > 0;

    const emises = fs.filter((f) => !STATUTS_NON_EMIS.has(f.statut));
    const retenues = facturesRetenues(fs);
    const retenuesIds = new Set(retenues.map((f) => f.id));

    // Une facture finale solde la FACTURATION — plus rien à mettre en facture.
    // Elle ne solde pas le chantier pour autant : tant que le client n'a pas
    // payé, il reste de l'argent à faire rentrer, et l'écran doit le montrer.
    const finaleEmise = retenues.some((f) => f.kind === 'finale');

    const signe = signeFacture;
    const montantFactureHt = dejaFactureHt(retenues);
    const engageHt = engagementHt(ch.montantTotalHt, signes, retenues);
    // On ne sait ce qu'il reste que sur un chantier dont le CRM porte les
    // documents ET dont l'engagement est connu. Sinon null : l'écran dira
    // « on ne sait pas », ce qui vaut mieux qu'un chiffre inventé.
    const engagementConnu = suiviParLeCrm && engageHt > 0;
    // Une facture finale émise ferme la facturation quoi qu'en dise
    // l'engagement estimé : il n'y a plus rien à mettre en facture, point.
    const resteAFacturerHt = finaleEmise
      ? 0
      : engagementConnu
        ? centimes(engageHt - montantFactureHt)
        : null;

    // Réclamé mais pas encore tombé sur le compte. C'est TTC : c'est TTC que le
    // client paie, et TTC que la banque verra.
    const resteAEncaisserTtc = centimes(
      retenues.reduce(
        (t, f) => t + signe(f) * (Math.abs(f.montantTtc) - Math.abs(f.montantPaye)),
        0,
      ),
    );
    const resteAEncaisserHt = centimes(
      retenues.reduce((t, f) => {
        const ttc = Math.abs(f.montantTtc);
        // Une facture à 0 € TTC ne peut pas être « partiellement » réglée : la
        // division serait un NaN qui contaminerait tout le total.
        const partRestante = ttc === 0 ? 0 : Math.max(0, 1 - Math.abs(f.montantPaye) / ttc);
        return t + signe(f) * Math.abs(f.montantHt) * partRestante;
      }, 0),
    );

    // QUAND UN CHANTIER QUITTE CET ÉCRAN : quand il n'y a plus rien à faire
    // rentrer. Ni à facturer, ni à encaisser.
    //
    // La règle d'avant le sortait dès qu'il était entièrement FACTURÉ. Un
    // chantier facturé et jamais payé disparaissait donc au moment précis où
    // il fallait le surveiller. Le suivi va maintenant jusqu'au bout :
    // signature, facture, encaissement.
    //
    // Un reste à facturer INCONNU (`null`) ne vaut pas « rien à facturer » :
    // on ne sort pas un chantier au motif qu'on ignore ce qu'il vaut.
    const plusRienAFacturer = resteAFacturerHt !== null && resteAFacturerHt <= 0.01;
    const plusRienAEncaisser = resteAEncaisserTtc <= 0.01;
    if (plusRienAFacturer && plusRienAEncaisser) continue;

    // « Engagé » = le client a dit oui. Un devis signé, une facture déjà émise,
    // ou un chantier commencé : dans les trois cas, il y a de quoi facturer.
    const engage = devisSigne || emises.length > 0 || ch.statut === 'en_cours';

    // Pas à l'agenda : on ne le retient que s'il est ENGAGÉ et que le CRM en
    // porte les documents. Sans la première condition on inonderait la liste
    // de chantiers dont personne n'attend rien ; sans la seconde on ferait
    // remonter les chantiers d'Extrabat, sans montant ni pièce à montrer.
    //
    // On n'exige PAS de connaître le reste à facturer. Cette exigence-là avait
    // fait disparaître de l'écran un chantier bien réel — un acompte émis, une
    // facture annulée, et aucun devis signé d'où tirer l'engagement. Un
    // chantier engagé qui n'est pas soldé se montre, quitte à afficher « ? » :
    // le cacher parce qu'on ne sait pas le chiffrer est le pire des deux.
    // Ceux qui SONT soldés sont déjà sortis plus haut.
    if (fin === undefined) {
      if (!engage) continue;
      if (!suiviParLeCrm) continue;
    }

    // À qui ira la facture. Le devis SIGNÉ fait foi : c'est lui qui décrit ce
    // qu'il reste à facturer. À défaut, une facture déjà émise le dit aussi
    // bien. Et on ne le signale que s'il diffère du client du chantier.
    const destinataire =
      signes.find((d) => d.contactId !== null)?.contactId ??
      retenues.find((f) => f.contactId !== null)?.contactId ??
      null;
    const contactFactureId =
      destinataire !== null && destinataire !== ch.contactId ? destinataire : null;

    const acompteEmis = emises.some((f) => f.kind === 'acompte');
    const acompteBrouillon =
      !acompteEmis && fs.some((f) => f.kind === 'acompte' && f.statut === 'brouillon');
    const interventionPassee = fin !== undefined && fin < maintenantMs;

    const documents: DocumentLie[] = [
      ...ds.map((d) => ({
        id: d.id,
        type: 'devis' as const,
        numero: d.numero,
        kind: '',
        statut: d.statut,
        montantHt: d.montantHt,
        montantTtc: d.montantTtc,
        compte: DEVIS_SIGNES.has(d.statut),
      })),
      ...fs.map((f) => ({
        id: f.id,
        type: 'facture' as const,
        numero: f.numero,
        kind: f.kind,
        statut: f.statut,
        montantHt: f.montantHt,
        montantTtc: f.montantTtc,
        compte: retenuesIds.has(f.id),
      })),
    ].sort((a, b) => a.type.localeCompare(b.type) || a.numero.localeCompare(b.numero, 'fr'));

    lignes.push({
      chantierId: ch.id,
      numero: ch.numero,
      libelle: ch.libelle,
      statut: ch.statut,
      contactId: ch.contactId,
      contactFactureId,
      finIntervention: fin === undefined ? null : new Date(fin).toISOString(),
      interventionPassee,
      joursDepuis: fin === undefined ? null : Math.floor((maintenantMs - fin) / JOUR_MS),
      montantEngageHt: engagementConnu ? engageHt : null,
      montantFactureTtc: centimes(
        retenues.reduce((t, f) => t + signe(f) * Math.abs(f.montantTtc), 0),
      ),
      montantFactureHt,
      resteAFacturerHt,
      resteAEncaisserTtc,
      resteAEncaisserHt,
      suiviParLeCrm,
      devisSigne,
      acompteEmis,
      acompteBrouillon,
      documents,
      motif: plusRienAFacturer
        ? 'attente_reglement'
        : fin === undefined
          ? 'engage_non_place'
          : interventionPassee
            ? 'intervention_passee'
            : 'intervention_a_venir',
    });
  }

  // Le plus urgent d'abord : le travail fait et non facturé, puis ce qui vient,
  // puis ce qui n'est pas encore posé — classé par montant, l'argent le plus
  // gros en tête.
  // Le plus urgent d'abord : le travail fait et pas encore RÉCLAMÉ passe avant
  // l'argent déjà réclamé qui n'est pas tombé — le premier n'existe nulle part
  // tant qu'on ne l'a pas facturé, le second est au moins acquis sur le papier.
  const rang: Record<MotifFacturation, number> = {
    intervention_passee: 0,
    attente_reglement: 1,
    intervention_a_venir: 2,
    engage_non_place: 3,
  };
  return lignes.sort(
    (a, b) =>
      rang[a.motif] - rang[b.motif] ||
      (b.joursDepuis ?? 0) - (a.joursDepuis ?? 0) ||
      (b.resteAFacturerHt ?? 0) - (a.resteAFacturerHt ?? 0),
  );
}

// ─── Contrôle avant émission ────────────────────────────────────────────────

/** Ce que le CRM peut dire d'un brouillon avant qu'il ne devienne définitif. */
export type VerdictEmission =
  | { risque: 'aucun' }
  /** Nul ne sait ce que vaut le chantier : on se tait plutôt que d'alarmer. */
  | { risque: 'engagement_inconnu' }
  | {
      risque: 'depassement';
      /** Ce que le chantier doit rapporter en tout, HT. */
      engageHt: number;
      /** Déjà facturé dessus, hors ce brouillon, HT. */
      dejaFactureHt: number;
      /** Ce qu'il resterait légitimement à facturer, HT. */
      resteAFacturerHt: number;
      /** Ce que ce brouillon facturerait, HT. */
      brouillonHt: number;
      /** De combien il dépasse. */
      ecartHt: number;
    };

/**
 * Ce brouillon facturerait-il plus qu'il ne reste à facturer sur son chantier ?
 *
 * LE PIÈGE QU'IL FERME. Convertir un devis en facture déduit les acomptes déjà
 * émis — mais au moment de la conversion, et à ce moment-là seulement. Un
 * brouillon fabriqué AVANT l'acompte garde le montant plein. L'émettre ensuite
 * refacture l'acompte une seconde fois, et le client reçoit une facture trop
 * élevée de tout l'acompte. Sur trois brouillons réels, l'écart allait de
 * 2 145,60 € à 3 978 € HT.
 *
 * On AVERTIT, on ne bloque pas : refacturer plus que le devis peut être
 * légitime (travaux supplémentaires acceptés de vive voix, révision de prix).
 * C'est à l'humain de trancher — mais en connaissance de cause.
 *
 * Le verdict porte sur une émission EN TOTALITÉ. Émis en acompte de 40 %, le
 * même brouillon ne facturerait que 40 % : c'est à l'écran de le dire.
 */
export function controlerEmission(
  brouillon: FactureRattachee,
  montantTotalChantierHt: number,
  facturesDuChantier: readonly FactureRattachee[],
  devisDuChantier: readonly DevisRattache[],
): VerdictEmission {
  // Un avoir a vocation à retirer de l'argent : le comparer à un reste à
  // facturer n'a aucun sens.
  if (brouillon.kind === 'avoir') return { risque: 'aucun' };

  // Le brouillon lui-même ne compte pas dans ce qui est DÉJÀ facturé — il ne
  // l'est pas encore. L'y laisser ferait comparer la facture à elle-même.
  const autres = facturesDuChantier.filter((f) => f.id !== brouillon.id);
  const retenues = facturesRetenues(autres);
  const deja = dejaFactureHt(retenues);
  const signes = devisDuChantier.filter((d) => DEVIS_SIGNES.has(d.statut));
  const engageHt = engagementHt(montantTotalChantierHt, signes, retenues);

  // Sans engagement connu, aucune comparaison n'est possible. Se taire vaut
  // mieux qu'un avertissement au hasard : une alerte qui crie à tort finit
  // par être ignorée quand elle a raison.
  if (engageHt <= 0) return { risque: 'engagement_inconnu' };

  const resteAFacturerHt = centimes(engageHt - deja);
  const brouillonHt = Math.abs(brouillon.montantHt);
  const ecartHt = centimes(brouillonHt - resteAFacturerHt);
  // Le seuil au centime évite qu'un arrondi de TVA déclenche l'alerte.
  if (ecartHt <= 0.01) return { risque: 'aucun' };

  return {
    risque: 'depassement',
    engageHt,
    dejaFactureHt: deja,
    resteAFacturerHt,
    brouillonHt,
    ecartHt,
  };
}
